import { DeleteOutlined, EditOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import { Button, Card, Popconfirm } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import "./BlockList.scss";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Locale } from "../../utils";
import {
  setSelectedBlock,
  removeBlock,
  updateBlockOrder,
} from "../../utils/redux/actions/CustomPage";
import { getBlocks, getSelectedBlock } from "../../utils/redux/reducers/CustomPage";
import { getBlockTypeLabel } from "./blockConfigs";

const i18n = Locale.getInstance();

const SortableBlockItem = ({ block }) => {
  const dispatch = useDispatch();
  const selectedBlock = useSelector(getSelectedBlock);
  const isSelected = selectedBlock?.id === block.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    dispatch(setSelectedBlock(block.id));
  };

  const handleDelete = () => {
    dispatch(removeBlock(block.id));
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className={`block-item ${isSelected ? "selected" : ""}`}
        onClick={handleClick}
      >
        <div className="block-item-header">
          <div className="block-drag-handle" {...attributes} {...listeners}>
            <FullscreenExitOutlined />
          </div>
          <div className="block-title">
            {getBlockTypeLabel(block.type, i18n)}
          </div>
          <div className="block-actions">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            />
            <Popconfirm
              title={i18n.t("sales.deleteWarning")}
              onConfirm={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              okText={i18n.t("base.modal.okText")}
              cancelText={i18n.t("base.modal.cancelText")}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </div>
        </div>
      </Card>
    </div>
  );
};

const BlockList = () => {
  const dispatch = useDispatch();
  const blocks = useSelector(getBlocks);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex((block) => block.id === active.id);
      const newIndex = blocks.findIndex((block) => block.id === over.id);

      const newBlocks = arrayMove(blocks, oldIndex, newIndex);
      dispatch(updateBlockOrder(newBlocks));
    }
  };

  return (
    <div className="block-list">
      <h3>{i18n.t("customPage.blocks")}</h3>
      {blocks.length === 0 ? (
        <div className="empty-state">{i18n.t("customPage.noBlocks")}</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map((block) => block.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="blocks-container">
              {blocks.map((block) => (
                <SortableBlockItem key={block.id} block={block} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default BlockList;

